import { useState, useEffect, useRef, useCallback } from 'react';
import {
  DocumentTextIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  CheckIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  LinkIcon,
  LinkSlashIcon,
  UserCircleIcon,
  MapPinIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { uploadDocument, deleteDocument } from '../../../services/documentStorageService';
import { useSchoolFirestore } from '../../../hooks/useSchoolFirestore';
import { useSchool } from '../../../contexts/SchoolContext';
import { useNotifications } from '../../../components/notifications/NotificationSystem';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import orgChartService from '../../../services/orgChartService';
import UserSearchCombobox from '../../../components/shared/UserSearchCombobox';

const DEFAULT_NOTIFY_CHANNELS = { email: true, inApp: true, telegram: false, sms: false };

const PlaceholderHint = ({ variables }) => {
  if (!variables || variables.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      {variables.map(v => (
        <code key={v} className="text-[10px] bg-base-200 px-1.5 py-0.5 rounded text-base-content/60">
          {`{${v}}`}
        </code>
      ))}
    </div>
  );
};

// Channel definitions
const CHANNELS = [
  { id: 'letter', label: 'Letter', icon: DocumentTextIcon },
  { id: 'mobile', label: 'Mobile', icon: DevicePhoneMobileIcon },
  { id: 'email', label: 'Email', icon: EnvelopeIcon },
];

function ChannelCard({ channel, isActive, hasContent, onClick }) {
  const Icon = channel.icon;
  return (
    <button
      onClick={onClick}
      className={`
        relative flex flex-col items-center justify-center gap-2
        w-24 h-24 rounded-xl border-2 transition-all cursor-pointer
        hover:shadow-md hover:scale-[1.02] active:scale-[0.98]
        ${isActive
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-base-300 bg-base-100 hover:border-base-content/20'
        }
      `}
    >
      <Icon className={`w-7 h-7 ${isActive ? 'text-primary' : 'text-base-content/60'}`} />
      <span className={`text-xs font-medium ${isActive ? 'text-primary' : 'text-base-content/70'}`}>
        {channel.label}
      </span>
      {hasContent && (
        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary" />
      )}
    </button>
  );
}

function LinkButton({ linked, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`
        relative flex items-center justify-center w-8 h-8 rounded-full
        transition-all self-center
        ${linked
          ? 'bg-primary/10 text-primary hover:bg-primary/20'
          : 'bg-base-200 text-base-content/30 hover:bg-base-300'
        }
      `}
      title={linked ? 'Linked — click to separate' : 'Separated — click to link'}
    >
      {linked ? (
        <LinkIcon className="w-4 h-4" />
      ) : (
        <LinkSlashIcon className="w-4 h-4" />
      )}
    </button>
  );
}

/**
 * Fetches the base notification template for a consequence type.
 * Looks up: messageTypeSettings/{typeId} → template field → messageTemplates/{template}
 * Falls back to school-specific then global messageTemplates/consequence_alert.
 */
async function fetchBaseTemplate(schoolId, typeId) {
  let templateName = 'consequence_alert';
  try {
    const settingsRef = doc(db, 'schools', schoolId, 'messageTypeSettings', typeId);
    const settingsSnap = await getDoc(settingsRef);
    if (settingsSnap.exists() && settingsSnap.data().template) {
      templateName = settingsSnap.data().template;
    }
  } catch (err) {
    console.warn('Could not fetch messageTypeSettings:', err);
  }

  // Try school-specific then global
  for (const path of [
    ['schools', schoolId, 'messageTemplates', templateName],
    ['messageTemplates', templateName]
  ]) {
    try {
      const ref = doc(db, ...path);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        return { ...snap.data(), _templateName: templateName };
      }
    } catch (err) {
      console.warn(`Could not fetch template at ${path.join('/')}:`, err);
    }
  }

  return null;
}

export default function ConsequenceTypeExpandedContent({ type, onTypeUpdated }) {
  const { update: updateWithSchool } = useSchoolFirestore();
  const { schoolId } = useSchool();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const [activeChannel, setActiveChannel] = useState(null);

  // Notify targets state
  const [notifyTargets, setNotifyTargets] = useState(type.notifyTargets || []);
  const [orgPositions, setOrgPositions] = useState([]);
  const [savingNotify, setSavingNotify] = useState(false);
  const [notifySaved, setNotifySaved] = useState(false);
  const [notifyDirty, setNotifyDirty] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState('');

  // Base template from Firestore
  const [baseTemplate, setBaseTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // Linked state — mobile and email share the same template by default
  // They're linked unless one has a separate override
  const [channelsLinked, setChannelsLinked] = useState(
    !type.emailTemplate && !type.telegramTemplate
      ? true
      : type.emailTemplate === type.telegramTemplate
  );

  // Document state
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Shared template state (when linked)
  const [sharedTemplate, setSharedTemplate] = useState(
    type.emailTemplate || type.telegramTemplate || ''
  );
  const [savingShared, setSavingShared] = useState(false);
  const [sharedSaved, setSharedSaved] = useState(false);
  const [sharedDirty, setSharedDirty] = useState(false);

  // Email template state (when unlinked)
  const hasEmailOverride = !!type.emailTemplate;
  const [emailTemplate, setEmailTemplate] = useState(type.emailTemplate || '');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailDirty, setEmailDirty] = useState(false);

  // Telegram template state (when unlinked)
  const hasTelegramOverride = !!type.telegramTemplate;
  const [telegramTemplate, setTelegramTemplate] = useState(type.telegramTemplate || '');
  const [savingTelegram, setSavingTelegram] = useState(false);
  const [telegramSaved, setTelegramSaved] = useState(false);
  const [telegramDirty, setTelegramDirty] = useState(false);

  // Load org chart positions on mount (for the position picker)
  useEffect(() => {
    if (!schoolId) return;
    orgChartService.getOrgCharts(schoolId)
      .then(charts => {
        const allPositions = orgChartService.getAllPositions(charts);
        const labels = [...new Set(allPositions.map(p => p.label).filter(Boolean))].sort();
        setOrgPositions(labels);
      })
      .catch(err => console.warn('[ConsequenceTypeExpandedContent] Could not load org positions:', err));
  }, [schoolId]);

  // Load the base template on mount
  useEffect(() => {
    if (!schoolId || !type.id) return;
    const typeId = type.value || type.id;
    setLoadingTemplate(true);
    fetchBaseTemplate(schoolId, typeId)
      .then(tmpl => {
        setBaseTemplate(tmpl);
        if (tmpl) {
          const baseBody = tmpl.body || '';
          const baseEmail = tmpl.emailText || baseBody;
          // Populate editors with base template if no per-type override
          if (!type.emailTemplate && !type.telegramTemplate) {
            setSharedTemplate(baseBody);
            setEmailTemplate(baseEmail);
            setTelegramTemplate(baseBody);
          } else if (!type.emailTemplate) {
            setEmailTemplate(baseEmail);
          } else if (!type.telegramTemplate) {
            setTelegramTemplate(baseBody);
          }
        }
      })
      .catch(err => console.error('Failed to load base template:', err))
      .finally(() => setLoadingTemplate(false));
  }, [schoolId, type.id, type.value]);

  // File upload handler
  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    const allowed = ['.pdf', '.doc', '.docx'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) {
      showError('Only PDF, DOC, and DOCX files are accepted');
      return;
    }

    setUploading(true);
    try {
      const result = await uploadDocument(file, schoolId, 'consequenceTypes', type.id);
      await updateWithSchool('consequenceTypes', type.id, {
        document: {
          ...result,
          uploadedAt: new Date().toISOString(),
          uploadedBy: user.uid
        }
      });
      showSuccess('Document uploaded');
      onTypeUpdated?.();
    } catch (err) {
      console.error('Upload failed:', err);
      showError('Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [schoolId, type.id, user.uid, updateWithSchool, showSuccess, showError, onTypeUpdated]);

  const handleInputUpload = (e) => handleFileUpload(e.target.files?.[0]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files?.[0]);
  }, [handleFileUpload]);

  const handleRemoveDocument = async () => {
    try {
      if (type.document?.storagePath) {
        await deleteDocument(type.document.storagePath);
      }
      await updateWithSchool('consequenceTypes', type.id, { document: null });
      showSuccess('Document removed');
      onTypeUpdated?.();
    } catch (err) {
      console.error('Remove failed:', err);
      showError('Failed to remove document');
    }
  };

  // Save handlers — linked mode saves both fields
  const handleSaveShared = async () => {
    setSavingShared(true);
    try {
      await updateWithSchool('consequenceTypes', type.id, {
        emailTemplate: sharedTemplate,
        telegramTemplate: sharedTemplate
      });
      setSharedSaved(true);
      setSharedDirty(false);
      setTimeout(() => setSharedSaved(false), 2000);
    } catch (err) {
      showError('Failed to save template');
    } finally {
      setSavingShared(false);
    }
  };

  const handleResetShared = async () => {
    const baseBody = baseTemplate?.body || '';
    setSharedTemplate(baseBody);
    setSavingShared(true);
    try {
      await updateWithSchool('consequenceTypes', type.id, {
        emailTemplate: '',
        telegramTemplate: ''
      });
      setSharedDirty(false);
      showSuccess('Reset to base template');
      onTypeUpdated?.();
    } catch (err) {
      showError('Failed to reset template');
    } finally {
      setSavingShared(false);
    }
  };

  const handleSaveEmail = async () => {
    setSavingEmail(true);
    try {
      await updateWithSchool('consequenceTypes', type.id, { emailTemplate });
      setEmailSaved(true);
      setEmailDirty(false);
      setTimeout(() => setEmailSaved(false), 2000);
    } catch (err) {
      showError('Failed to save email template');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleResetEmail = async () => {
    const baseBody = baseTemplate?.emailText || baseTemplate?.body || '';
    setEmailTemplate(baseBody);
    setSavingEmail(true);
    try {
      await updateWithSchool('consequenceTypes', type.id, { emailTemplate: '' });
      setEmailDirty(false);
      showSuccess('Reset to base template');
      onTypeUpdated?.();
    } catch (err) {
      showError('Failed to reset template');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSaveTelegram = async () => {
    setSavingTelegram(true);
    try {
      await updateWithSchool('consequenceTypes', type.id, { telegramTemplate });
      setTelegramSaved(true);
      setTelegramDirty(false);
      setTimeout(() => setTelegramSaved(false), 2000);
    } catch (err) {
      showError('Failed to save Telegram template');
    } finally {
      setSavingTelegram(false);
    }
  };

  const handleResetTelegram = async () => {
    const baseBody = baseTemplate?.body || '';
    setTelegramTemplate(baseBody);
    setSavingTelegram(true);
    try {
      await updateWithSchool('consequenceTypes', type.id, { telegramTemplate: '' });
      setTelegramDirty(false);
      showSuccess('Reset to base template');
      onTypeUpdated?.();
    } catch (err) {
      showError('Failed to reset template');
    } finally {
      setSavingTelegram(false);
    }
  };

  // Auto-save notify targets — no Save button. Every add/remove/channel change
  // creates a fresh array, so this effect persists each edit the moment it lands,
  // mirroring the weekly brief. The mount ref skips the initial load so opening a
  // type doesn't trigger a redundant write.
  const notifyMountRef = useRef(true);
  useEffect(() => {
    if (notifyMountRef.current) { notifyMountRef.current = false; return; }
    let cancelled = false;
    setSavingNotify(true);
    updateWithSchool('consequenceTypes', type.id, { notifyTargets })
      .then(() => {
        if (cancelled) return;
        setNotifyDirty(false);
        setNotifySaved(true);
        setTimeout(() => setNotifySaved(false), 2000);
        onTypeUpdated?.();
      })
      .catch(() => { if (!cancelled) showError('Failed to save notification targets'); })
      .finally(() => { if (!cancelled) setSavingNotify(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifyTargets]);

  const handleLinkToggle = () => {
    if (channelsLinked) {
      const confirmed = window.confirm(
        'Unlink mobile and email? They will have separate templates.'
      );
      if (!confirmed) return;
      setChannelsLinked(false);
      setEmailTemplate(sharedTemplate);
      setTelegramTemplate(sharedTemplate);
    } else {
      const merged = emailTemplate || telegramTemplate || sharedTemplate;
      setSharedTemplate(merged);
      setChannelsLinked(true);
      setActiveChannel(activeChannel === 'email' || activeChannel === 'mobile' ? 'mobile' : activeChannel);
    }
  };

  const toggleChannel = (channelId) => {
    // If linked and clicking email or mobile, show the linked editor
    if (channelsLinked && (channelId === 'email' || channelId === 'mobile')) {
      setActiveChannel(prev =>
        (prev === 'email' || prev === 'mobile') ? null : channelId
      );
    } else {
      setActiveChannel(prev => prev === channelId ? null : channelId);
    }
  };

  const hasSharedOverride = hasEmailOverride || hasTelegramOverride;
  const channelHasContent = {
    email: hasEmailOverride,
    mobile: hasTelegramOverride,
    letter: !!type.document?.fileUrl,
  };

  // All variables available in the notification engine
  const availableVariables = [
    'studentName', 'studentFullName', 'parentName',
    'consequenceType', 'offenseName', 'description',
    'dayName', 'date', 'time',
    'teacherName', 'teacherNote', 'schoolName',
    'pronoun', 'pronounPossessive',
    'coreValueLine', 'appLink',
  ];

  // Is the linked editor active?
  const linkedEditorActive = channelsLinked && (activeChannel === 'mobile' || activeChannel === 'email');

  return (
    <div className="p-6">
      {/* Channel Cards — horizontal row with link icon */}
      <div className="flex gap-3 justify-center items-center overflow-x-auto pb-2">
        <ChannelCard
          channel={CHANNELS[0]}
          isActive={activeChannel === 'letter'}
          hasContent={channelHasContent.letter}
          onClick={() => toggleChannel('letter')}
        />
        <ChannelCard
          channel={CHANNELS[1]}
          isActive={linkedEditorActive || (!channelsLinked && activeChannel === 'mobile')}
          hasContent={channelsLinked ? hasSharedOverride : channelHasContent.mobile}
          onClick={() => toggleChannel('mobile')}
        />
        <LinkButton linked={channelsLinked} onClick={handleLinkToggle} />
        <ChannelCard
          channel={CHANNELS[2]}
          isActive={linkedEditorActive || (!channelsLinked && activeChannel === 'email')}
          hasContent={channelsLinked ? hasSharedOverride : channelHasContent.email}
          onClick={() => toggleChannel('email')}
        />
      </div>

      {/* Notify section — staff notification targets for this consequence type */}
      <div className="mt-6 pt-6 border-t border-base-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold">Notify</h4>
          <p className="text-xs text-base-content/50">Staff notified when this type is issued</p>
        </div>

        {/* Add staff member */}
        <div className="mb-3">
          <p className="text-xs text-base-content/60 mb-1">Add staff member</p>
          <UserSearchCombobox
            userTypes={['staff']}
            excludeUserIds={notifyTargets.filter(t => t.type === 'user').map(t => t.value)}
            placeholder="Search staff..."
            onSelect={(user) => {
              setNotifyTargets(prev => [
                ...prev,
                {
                  type: 'user',
                  value: user.id,
                  label: user.displayName || user.email || user.id,
                  channels: { ...DEFAULT_NOTIFY_CHANNELS },
                  scope: null,
                },
              ]);
              setNotifyDirty(true);
            }}
          />
        </div>

        {/* Add org chart position */}
        {orgPositions.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-base-content/60 mb-1">Add org chart position</p>
            <select
              className="select select-bordered select-sm w-full"
              value={selectedPosition}
              onChange={(e) => {
                const label = e.target.value;
                if (!label) return;
                setNotifyTargets(prev => [
                  ...prev,
                  {
                    type: 'position',
                    value: label,
                    label,
                    channels: { ...DEFAULT_NOTIFY_CHANNELS },
                    scope: null,
                  },
                ]);
                setNotifyDirty(true);
                setSelectedPosition('');
              }}
            >
              <option value="">Select a position…</option>
              {orgPositions.map(label => (
                <option
                  key={label}
                  value={label}
                  disabled={notifyTargets.some(t => t.type === 'position' && t.value === label)}
                >
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Target list */}
        {notifyTargets.length > 0 ? (
          <div className="space-y-2 mb-4">
            {notifyTargets.map((target, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-base-200 rounded-lg">
                {target.type === 'user' ? (
                  <UserCircleIcon className="w-5 h-5 text-primary flex-shrink-0" />
                ) : (
                  <MapPinIcon className="w-5 h-5 text-secondary flex-shrink-0" />
                )}
                <span className="flex-1 text-sm truncate">{target.label || target.value}</span>
                <span className={`badge badge-xs ${target.type === 'user' ? 'badge-primary' : 'badge-secondary'}`}>
                  {target.type === 'user' ? 'User' : 'Position'}
                </span>

                {/* Channel toggles */}
                <label className="flex items-center gap-1 text-xs" title="Email">
                  <input
                    type="checkbox"
                    className="toggle toggle-xs"
                    checked={target.channels?.email !== false}
                    onChange={(e) => {
                      const updated = [...notifyTargets];
                      updated[idx] = { ...updated[idx], channels: { ...updated[idx].channels, email: e.target.checked } };
                      setNotifyTargets(updated);
                      setNotifyDirty(true);
                    }}
                  />
                  <span>Email</span>
                </label>
                <label className="flex items-center gap-1 text-xs" title="In-app">
                  <input
                    type="checkbox"
                    className="toggle toggle-xs"
                    checked={target.channels?.inApp !== false}
                    onChange={(e) => {
                      const updated = [...notifyTargets];
                      updated[idx] = { ...updated[idx], channels: { ...updated[idx].channels, inApp: e.target.checked } };
                      setNotifyTargets(updated);
                      setNotifyDirty(true);
                    }}
                  />
                  <span>App</span>
                </label>
                <label className="flex items-center gap-1 text-xs opacity-40" title="Coming soon">
                  <input
                    type="checkbox"
                    className="toggle toggle-xs"
                    checked={target.channels?.telegram === true}
                    disabled
                    onChange={(e) => {
                      const updated = [...notifyTargets];
                      updated[idx] = { ...updated[idx], channels: { ...updated[idx].channels, telegram: e.target.checked } };
                      setNotifyTargets(updated);
                      setNotifyDirty(true);
                    }}
                  />
                  <span>Telegram</span>
                </label>
                <label className="flex items-center gap-1 text-xs opacity-40" title="Coming soon">
                  <input
                    type="checkbox"
                    className="toggle toggle-xs"
                    checked={target.channels?.sms === true}
                    disabled
                    onChange={(e) => {
                      const updated = [...notifyTargets];
                      updated[idx] = { ...updated[idx], channels: { ...updated[idx].channels, sms: e.target.checked } };
                      setNotifyTargets(updated);
                      setNotifyDirty(true);
                    }}
                  />
                  <span>SMS</span>
                </label>

                {/* Remove */}
                <button
                  onClick={() => {
                    setNotifyTargets(prev => notifyTargets.filter((_, i) => i !== idx));
                    setNotifyDirty(true);
                  }}
                  className="btn btn-ghost btn-xs text-error"
                  title="Remove"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-base-content/40 mb-4">No staff configured. Add staff or a position above.</p>
        )}

        {/* Auto-saves on every change — status only, no button */}
        <div className="flex justify-end h-5">
          {savingNotify ? (
            <span className="text-xs text-base-content/40 flex items-center gap-1">
              <span className="loading loading-spinner loading-xs" /> Saving…
            </span>
          ) : notifySaved ? (
            <span className="text-xs text-success flex items-center gap-1">
              <CheckIcon className="w-3.5 h-3.5" /> Saved
            </span>
          ) : null}
        </div>
      </div>

      {/* Expanded channel editor */}
      {activeChannel && (
        <div className="mt-4 pt-4 border-t border-base-200">

          {/* Linked Mobile + Email Editor */}
          {linkedEditorActive && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DevicePhoneMobileIcon className="w-4 h-4" />
                  <LinkIcon className="w-3 h-3 text-base-content/30" />
                  <EnvelopeIcon className="w-4 h-4" />
                  <span className="text-sm font-semibold">Mobile & Email Template</span>
                  {hasSharedOverride && (
                    <span className="badge badge-xs badge-primary">Customized</span>
                  )}
                </div>
                {hasSharedOverride && (
                  <button
                    onClick={handleResetShared}
                    className="btn btn-ghost btn-xs gap-1 text-base-content/50"
                    title="Reset to base template"
                  >
                    <ArrowPathIcon className="w-3.5 h-3.5" />
                    Reset
                  </button>
                )}
              </div>
              {loadingTemplate ? (
                <div className="flex justify-center py-4">
                  <span className="loading loading-spinner loading-sm" />
                </div>
              ) : (
                <>
                  <textarea
                    value={sharedTemplate}
                    onChange={(e) => {
                      setSharedTemplate(e.target.value);
                      setSharedSaved(false);
                      setSharedDirty(true);
                    }}
                    className="textarea textarea-bordered w-full text-sm font-mono leading-relaxed"
                    rows={8}
                  />
                  <div className="flex items-start justify-between gap-4">
                    <PlaceholderHint variables={availableVariables} />
                    <button
                      onClick={handleSaveShared}
                      disabled={savingShared || sharedSaved || !sharedDirty}
                      className={`btn btn-sm flex-shrink-0 ${sharedSaved ? 'btn-success' : 'btn-primary'}`}
                    >
                      {savingShared ? (
                        <span className="loading loading-spinner loading-xs" />
                      ) : sharedSaved ? (
                        <><CheckIcon className="w-4 h-4" /> Saved</>
                      ) : (
                        'Save'
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Email Editor (unlinked) */}
          {!channelsLinked && activeChannel === 'email' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <EnvelopeIcon className="w-4 h-4" />
                  <span className="text-sm font-semibold">Email Template</span>
                  {hasEmailOverride && (
                    <span className="badge badge-xs badge-primary">Customized</span>
                  )}
                </div>
                {hasEmailOverride && (
                  <button
                    onClick={handleResetEmail}
                    className="btn btn-ghost btn-xs gap-1 text-base-content/50"
                    title="Reset to base template"
                  >
                    <ArrowPathIcon className="w-3.5 h-3.5" />
                    Reset
                  </button>
                )}
              </div>
              {loadingTemplate ? (
                <div className="flex justify-center py-4">
                  <span className="loading loading-spinner loading-sm" />
                </div>
              ) : (
                <>
                  <textarea
                    value={emailTemplate}
                    onChange={(e) => {
                      setEmailTemplate(e.target.value);
                      setEmailSaved(false);
                      setEmailDirty(true);
                    }}
                    className="textarea textarea-bordered w-full text-sm font-mono leading-relaxed"
                    rows={8}
                  />
                  <div className="flex items-start justify-between gap-4">
                    <PlaceholderHint variables={availableVariables} />
                    <button
                      onClick={handleSaveEmail}
                      disabled={savingEmail || emailSaved || !emailDirty}
                      className={`btn btn-sm flex-shrink-0 ${emailSaved ? 'btn-success' : 'btn-primary'}`}
                    >
                      {savingEmail ? (
                        <span className="loading loading-spinner loading-xs" />
                      ) : emailSaved ? (
                        <><CheckIcon className="w-4 h-4" /> Saved</>
                      ) : (
                        'Save'
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Mobile Editor (unlinked) */}
          {!channelsLinked && activeChannel === 'mobile' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DevicePhoneMobileIcon className="w-4 h-4" />
                  <span className="text-sm font-semibold">Mobile Notification</span>
                  {hasTelegramOverride && (
                    <span className="badge badge-xs badge-primary">Customized</span>
                  )}
                </div>
                {hasTelegramOverride && (
                  <button
                    onClick={handleResetTelegram}
                    className="btn btn-ghost btn-xs gap-1 text-base-content/50"
                    title="Reset to base template"
                  >
                    <ArrowPathIcon className="w-3.5 h-3.5" />
                    Reset
                  </button>
                )}
              </div>
              {loadingTemplate ? (
                <div className="flex justify-center py-4">
                  <span className="loading loading-spinner loading-sm" />
                </div>
              ) : (
                <>
                  <textarea
                    value={telegramTemplate}
                    onChange={(e) => {
                      setTelegramTemplate(e.target.value);
                      setTelegramSaved(false);
                      setTelegramDirty(true);
                    }}
                    className="textarea textarea-bordered w-full text-sm font-mono leading-relaxed"
                    rows={6}
                  />
                  <div className="flex items-start justify-between gap-4">
                    <PlaceholderHint variables={availableVariables} />
                    <button
                      onClick={handleSaveTelegram}
                      disabled={savingTelegram || telegramSaved || !telegramDirty}
                      className={`btn btn-sm flex-shrink-0 ${telegramSaved ? 'btn-success' : 'btn-primary'}`}
                    >
                      {savingTelegram ? (
                        <span className="loading loading-spinner loading-xs" />
                      ) : telegramSaved ? (
                        <><CheckIcon className="w-4 h-4" /> Saved</>
                      ) : (
                        'Save'
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Letter / Document Editor */}
          {activeChannel === 'letter' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="w-4 h-4" />
                <span className="text-sm font-semibold">Letter Document</span>
              </div>

              {type.document?.fileUrl ? (
                <div className="flex items-center gap-3 p-4 bg-base-200 rounded-xl">
                  <DocumentTextIcon className="w-6 h-6 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{type.document.fileName}</p>
                    <p className="text-xs text-base-content/50">
                      {type.document.fileType || 'Document'}
                    </p>
                  </div>
                  <a
                    href={type.document.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ghost btn-sm gap-1"
                  >
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                    View
                  </a>
                  <button
                    onClick={handleRemoveDocument}
                    className="btn btn-ghost btn-sm text-error gap-1"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Remove
                  </button>
                </div>
              ) : (
                <div
                  ref={dropZoneRef}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`
                    flex flex-col items-center justify-center gap-3 p-8
                    rounded-xl border-2 border-dashed cursor-pointer
                    transition-all
                    ${dragOver
                      ? 'border-primary bg-primary/5 scale-[1.01]'
                      : 'border-base-300 hover:border-base-content/30 hover:bg-base-200/50'
                    }
                    ${uploading ? 'opacity-60 pointer-events-none' : ''}
                  `}
                >
                  {uploading ? (
                    <span className="loading loading-spinner loading-md text-primary" />
                  ) : (
                    <CloudArrowUpIcon className={`w-10 h-10 ${dragOver ? 'text-primary' : 'text-base-content/30'}`} />
                  )}
                  <div className="text-center">
                    <p className={`text-sm font-medium ${dragOver ? 'text-primary' : 'text-base-content/60'}`}>
                      {dragOver ? 'Drop file here' : 'Drag & drop a file here'}
                    </p>
                    <p className="text-xs text-base-content/40 mt-1">
                      or click to browse — PDF, DOC, DOCX
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleInputUpload}
                    className="hidden"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
